import React, { useEffect, useRef } from 'react';
import { usePipecatClientMediaTrack } from '@pipecat-ai/client-react';

type ParticipantType = Parameters<typeof usePipecatClientMediaTrack>[1];

interface ConversationVisualizerProps {
  participantType: ParticipantType;
  barColor?: string;
  barCount?: number;
  barGap?: number;
  barWidth?: number;
  barMaxHeight?: number;
  // Fraction of each half of the visualiser (edge → centre) over which the
  // height scale ramps up; the rest is a flat plateau. 0.5 on 36 bars means
  // 9 ramp bars per side and an 18-bar plateau, i.e. a /‾‾‾\ envelope.
  edgeRamp?: number;
  // Max height (px) of the very first and last bars; the ramp rises
  // linearly from here to barMaxHeight.
  edgeMinHeight?: number;
  className?: string;
}

// Port of voice-ui-kit's VoiceVisualizer (centre origin, round caps, no peaks)
// with a per-bar max height so energy is focused towards the middle. Each bar's
// live level is scaled to its own max (not clamped), so edge bars keep their
// natural movement, just smaller.
export const ConversationVisualizer: React.FC<ConversationVisualizerProps> = ({
  participantType,
  barColor = 'black',
  barCount = 36,
  barGap = 2,
  barWidth = 8,
  barMaxHeight = 96,
  edgeRamp = 0.5,
  edgeMinHeight = 1,
  className,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const track = usePipecatClientMediaTrack('audio', participantType);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const scaleFactor = 2;
    const width = barCount * barWidth + (barCount - 1) * barGap;
    const height = barMaxHeight;
    canvas.width = width * scaleFactor;
    canvas.height = height * scaleFactor;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.setTransform(scaleFactor, 0, 0, scaleFactor, 0, 0);
    ctx.lineCap = 'round';

    const radius = barWidth / 2;
    const centreY = height / 2;
    const halfSpan = (barCount - 1) / 2;
    const maxHeights = Array.from({ length: barCount }, (_, i) => {
      const fromEdge = halfSpan > 0 ? Math.min(i, barCount - 1 - i) / halfSpan : 1;
      const ramp = edgeRamp > 0 ? Math.min(1, fromEdge / edgeRamp) : 1;
      return edgeMinHeight + (barMaxHeight - edgeMinHeight) * ramp;
    });

    const drawDot = (x: number) => {
      ctx.beginPath();
      ctx.arc(x + radius, centreY, radius, 0, 2 * Math.PI);
      ctx.fillStyle = barColor;
      ctx.fill();
    };

    const drawIdle = () => {
      ctx.clearRect(0, 0, width, height);
      for (let i = 0; i < barCount; i++) drawDot(i * (barWidth + barGap));
    };

    if (!track) {
      drawIdle();
      return;
    }

    const audioContext = new AudioContext();
    const source = audioContext.createMediaStreamSource(new MediaStream([track]));
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 1024;
    source.connect(analyser);
    const frequencyData = new Uint8Array(analyser.frequencyBinCount);

    // Mel-spaced frequency bands, matching voice-ui-kit.
    const minFreq = barCount > 20 ? 200 : 80;
    const melMin = 2595 * Math.log10(1 + minFreq / 700);
    const melMax = 2595 * Math.log10(1 + 10000 / 700);
    const melStep = (melMax - melMin) / barCount;
    const melToHz = (mel: number) => 700 * (Math.pow(10, mel / 2595) - 1);
    const toBin = (freq: number) =>
      Math.round((freq / (audioContext.sampleRate / 2)) * (analyser.frequencyBinCount - 1));
    const bands = Array.from({ length: barCount }, (_, i) => {
      const startBin = toBin(melToHz(melMin + i * melStep));
      const endBin = toBin(melToHz(melMin + (i + 1) * melStep));
      return { startBin, endBin: Math.max(endBin, startBin + 1), smoothValue: 0 };
    });

    let frame = 0;
    const draw = () => {
      analyser.getByteFrequencyData(frequencyData);
      ctx.clearRect(0, 0, width, height);

      let isActive = false;
      bands.forEach((band, i) => {
        let sum = 0;
        for (let b = band.startBin; b < band.endBin; b++) sum += frequencyData[b];
        const bandValue = sum / (band.endBin - band.startBin);
        if (bandValue < 1) {
          band.smoothValue = Math.max(band.smoothValue - 1, 0);
        } else {
          band.smoothValue += (bandValue - band.smoothValue) * 0.2;
          isActive = true;
        }

        const x = i * (barWidth + barGap);
        if (band.smoothValue <= 0) {
          drawDot(x);
          return;
        }
        const barHeight = (band.smoothValue / 255) * maxHeights[i];
        const yTop = Math.max(centreY - barHeight / 2, radius);
        const yBottom = Math.min(centreY + barHeight / 2, height - radius);
        ctx.beginPath();
        ctx.moveTo(x + radius, yTop);
        ctx.lineTo(x + radius, yBottom);
        ctx.lineWidth = barWidth;
        ctx.strokeStyle = barColor;
        ctx.stroke();
      });

      if (!isActive) drawIdle();
      frame = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(frame);
      void audioContext.close();
    };
  }, [track, barColor, barCount, barGap, barWidth, barMaxHeight, edgeRamp, edgeMinHeight]);

  return <canvas ref={canvasRef} className={className} />;
};

export default ConversationVisualizer;
